import { useRef, useState } from 'react';

import { Ic } from './Icons';
import { cn } from '../lib/classnames';
import './SearchRow.css';

/** Two-mode search row that mirrors the prototype behaviour:
 *
 *  * **Collapsed** — the magnifier icon + a hint string. Tap anywhere
 *    in the row (Phase 4.11) — icon, hint text, or the empty space —
 *    to open. Cursor signals it via ``cursor: text``.
 *  * **Expanded** — text input + ``×`` clear button. Pressing Enter
 *    commits the value to the search-history dropdown via the
 *    optional ``onCommit`` callback (Phase 4.11). When the input is
 *    focused and empty and the parent passed a non-empty ``history``,
 *    a dropdown of up to three recent queries appears.
 *
 *  The "found N of M" meta line below the row is rendered as a
 *  second component (``SearchMeta``) so callers can conditionally
 *  include it without nesting state. */
export function SearchRow({
  open,
  value,
  history = [],
  onToggle,
  onChange,
  onCommit,
  onPickFromHistory,
}: {
  open: boolean;
  value: string;
  /** Phase 4.11 — recent queries to surface when input is focused
   *  and empty. Up to 3 entries; the dropdown is hidden when this
   *  is empty or the input has any non-empty value. */
  history?: readonly string[];
  onToggle: () => void;
  onChange: (next: string) => void;
  /** Phase 4.11 — invoked when the user presses Enter inside the
   *  input. Parent uses it to push the current ``value`` into the
   *  search-history store. Optional so older call sites still work. */
  onCommit?: (value: string) => void;
  /** Phase 4.11 — invoked when the user picks an entry from the
   *  history dropdown. The parent typically sets ``value`` to the
   *  picked query AND adds it back into history (move-to-front). */
  onPickFromHistory?: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  // Phase 4.11 — click-anywhere-to-open / focus. Three cases:
  //   1. Collapsed → toggle open. (The expanded branch will autofocus
  //      its input on mount.)
  //   2. Expanded but input lost focus → focus it again so typing
  //      starts immediately.
  //   3. Expanded and already focused → no-op (user clicked twice).
  // The icon's own ``<button>`` retains its onClick for keyboard +
  // screen-reader users — they tab to the button and hit space.
  const handleRowClick = () => {
    if (!open) {
      onToggle();
      return;
    }
    inputRef.current?.focus();
  };

  // Show the dropdown only on focus+empty-value+non-empty-history.
  // Three nested guards instead of a single boolean so each axis
  // is independently mockable in tests.
  const showHistory = open && focused && !value && history.length > 0;

  return (
    <div className="feed-search-row-wrapper">
      <div
        className="feed-search-row"
        onClick={handleRowClick}
        role={open ? undefined : 'button'}
        aria-label={open ? undefined : 'Открыть поиск'}
      >
        <button
          className={cn('feed-search-icon-btn', open && 'active')}
          onClick={(e) => {
            // Stop the click from bubbling to the row wrapper. Without
            // this, the wrapper's onClick fires AFTER the button's
            // onToggle and re-toggles (collapse → open → collapse) on
            // a single icon click.
            e.stopPropagation();
            onToggle();
          }}
          title="поиск по коду станции, ID или тегу"
          aria-label="Поиск"
        >
          <Ic.search />
        </button>
        {open ? (
          <>
            <input
              ref={inputRef}
              type="text"
              className="feed-search-input"
              placeholder='WTZR, 142, phase1, "точный_тег"...'
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => {
                // Defer blur slightly so a click on a history-dropdown
                // button registers (the button's own onMouseDown also
                // calls preventDefault, but the timeout is the safer
                // belt-and-suspenders).
                setTimeout(() => setFocused(false), 120);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onCommit?.(value);
                  inputRef.current?.blur();
                }
              }}
              autoFocus
            />
            {value && (
              <button
                className="feed-search-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
                aria-label="Очистить"
              >
                ×
              </button>
            )}
          </>
        ) : (
          <span className="feed-search-hint">
            поиск по станции, ID или тегу. "точный_тег" — точное совпадение
          </span>
        )}
      </div>
      {showHistory && (
        <div className="search-history-dropdown" role="listbox">
          <div className="search-history-hint">недавние запросы</div>
          {history.map((q) => (
            <button
              key={q}
              type="button"
              role="option"
              aria-selected={false}
              className="search-history-item"
              // ``onMouseDown`` instead of ``onClick`` because the
              // input's onBlur (which dismisses the dropdown) fires
              // before a regular click would. mousedown beats blur.
              onMouseDown={(e) => {
                e.preventDefault();
                onPickFromHistory?.(q);
                inputRef.current?.focus();
              }}
            >
              <span className="search-history-glyph">🕒</span>
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** "found X of Y" line below the search row. Pure-presentation. */
export function SearchMeta({ found, total }: { found: number; total: number }) {
  return (
    <div className="feed-search-meta">
      найдено: {found} из {total}
    </div>
  );
}
